import {
  FormEvent,
  FunctionComponent,
  useState,
  useEffect,
  useRef,
} from "react";
import CustomModal from "../../CustomModal";
import { useAppHandlers } from "../../../../services/actions/app/useAppHandlers";
import Row from "../../../Row";
import Text from "../../../Text";
import styled from "styled-components";
import Column from "../../../Column";
import Checkbox from "../../../Checkbox";
import LoadingSpinner from "../../../LoadingSpinner";
import { useConnectionsHandlers } from "../../../../services/actions/connections/useConnectionsHandlers";
import { IConnection } from "../../../../interfaces/models/connection";
import { formatCurrency, formatPriceTwoDecimals } from "../../../../utils";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import CurrencySelect from "../../../../pages/billing/components/CurrencySelect";
import { useCSVEnrichmentHandlers } from "../../../../services/actions/csv-enrichment/useCSVEnrichmentHandlers";
import PaymentMethods from "../../../../pages/billing/components/PaymentMethods";
import ActionText from "../../../ActionText";
import { useAuthStore } from "../../../../stores/auth/useAuthStore";
import { useBillingHandlers } from "../../../../services/actions/billing/useBillingHandlers";
import Select from "../../../select/Select";
import { useWorkspaceHandlers } from "../../../../services/actions/workspace/useWorkspaceHandlers";
import SvgFragment from "../../../../fragments/SvgFragment";
import Tag from "../../../Tag";
import { styles } from "../../../../styles/themes/style";

const stripePromise = loadStripe(
  `${process.env.REACT_APP_STRIPE_PUBLISHABLE_API_KEY}`
);

interface ICalculateEnrichmentModalProps {
  isAllSelected?: boolean;
  selectedConnections?: IConnection[];
  csvRows?: any[];
  onClickHandler: (
    selectedConnections: IConnection[],
    privateEmailChecked: boolean,
    businessEmailChecked: boolean,
    phoneChecked: boolean,
    enrichedTransactionId: string
  ) => void;
  phoneChecked?: boolean;
  privateEmailChecked?: boolean;
  businessEmailChecked?: boolean;
  isBuyCredits?: boolean;
  showConfetti?: () => void;
  noCreditsLeft?: boolean;
  onEnrichedTransactionChange?: (
    enrichedTransaction: IEnrichedTransaction
  ) => void;
}

interface ICalculatedPrice {
  phoneUnitPrice?: number;
  emailUnitPrice?: number;
  phonePrice?: number;
  privateEmailPrice?: number;
  businessEmailPrice?: number;
  totalPrice?: number;
  stripePrice?: number;
  privateEmailsCount?: number;
  businessEmailsCount?: number;
  phonesCount?: number;
  currency?: string;
}

interface ICreditOption {
  value: string;
  label: string;
  price: number;
  currency: string;
  priceId: string;
  productId: string;
  stripePrice: number;
}

export interface IEnrichedTransaction {
  _id: string;
  enriched: boolean;
  isCsv: boolean;
  businessEmailsCount: number;
  privateEmailsCount: number;
  phonesCount: number;
  privateEmailsCountAfterEnrichment: number;
  businessEmailsCountAfterEnrichment: number;
  phonesCountAfterEnrichment: number;
  enrichedData: any[];
  privateEmailChecked: boolean;
  businessEmailChecked: boolean;
  phoneChecked: boolean;
}

const CalculateEnrichmentModal: FunctionComponent<
  ICalculateEnrichmentModalProps
> = ({
  isAllSelected,
  selectedConnections,
  csvRows,
  phoneChecked,
  privateEmailChecked,
  businessEmailChecked,
  isBuyCredits,
  showConfetti,
  noCreditsLeft,
  onClickHandler,
  onEnrichedTransactionChange,
}) => {
  const {
    hideModal,
    showSuccessToast,
    showErrorToast,
    showRemovalScheduledToast,
    hideToast,
    setEnrichmentCompleteModalHandler,
  } = useAppHandlers();
  enum EnrichmentStep {
    STEP_CHECK_FIELDS = 1,
    STEP_BUY_CREDITS = 2,
    STEP_PAYMENT_DETAILS = 3,
    STEP_PAYMENT_SUCCESS = 4,
    STEP_NO_CREDITS_LEFT = 5,
  }
  const [step, setStep] = useState<EnrichmentStep>(
    EnrichmentStep.STEP_CHECK_FIELDS
  );

  // Determine if any flags are set
  const isAnyFlagSet =
    phoneChecked || privateEmailChecked || businessEmailChecked;

  // Initialize state based on whether any flags are set
  const [phoneCheckedState, setPhoneChecked] = useState(
    isAnyFlagSet ? phoneChecked : true
  );
  const [privateEmailCheckedState, setPrivateEmailChecked] = useState(
    isAnyFlagSet ? privateEmailChecked : true
  );
  const [businessEmailCheckedState, setBusinessEmailChecked] = useState(
    isAnyFlagSet ? businessEmailChecked : true
  );

  const enrichedConnectionsCountRef = useRef(0);
  const toastClosedRef = useRef(false);

  const [calculatedPrice, setCalculatedPrice] = useState<
    ICalculatedPrice | undefined
  >(undefined);
  const [invoiceId, setInvoiceId] = useState("");
  const [calculationInProgress, setCalculationInProgress] = useState(false);
  const [currency, setCurrency] = useState("usd");
  const [enrichmentInProgress, setEnrichmentInProgress] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | undefined
  >(undefined);
  const {
    store: { workspace },
  } = useAuthStore();

  const [estimatingCreditsDone, setEstimatingCreditsDone] = useState(false);
  const [estimatedCredits, setEstimatedCredits] = useState(0);
  const [enrichedTransactionId, setEnrichedTransactionId] = useState("");
  const [enrichedTransaction, setEnrichedTransaction] =
    useState<IEnrichedTransaction | null>(null);
  const [selectedCreditOption, setSelectedCreditOption] =
    useState<ICreditOption>({
      label: "",
      value: "",
      price: 0,
      currency: "usd",
      priceId: "",
      productId: "",
      stripePrice: 0,
    });

  const [isStripePayment, setIsStripePayment] = useState(false);
  const [creditOptions, setCreditOptions] = useState<ICreditOption[]>([]);
  const [creditOptionsLoading, setCreditOptionsLoading] = useState(true);
  const { getSetupIntentHandler, voidInvoiceRequestHandler } =
    useBillingHandlers();
  const [clientSecret, setClientSecret] = useState("");

  const {
    getEnrichedConnectionsHandler,
    estimateEnrichmentCreditsHandler,
    enrichConnectionsHandler,
    getEnrichedTransactionByIdHandler,
    buyCreditsInvoiceHandler,
    buyCreditsInvoiceWithoutEnrichedTransactionHandler,
  } = useConnectionsHandlers();
  const { getCreditOptionsHandler } = useCSVEnrichmentHandlers();
  const { refetchWorkspace } = useWorkspaceHandlers();

  //const [modalVisible, setModalVisible] = useState(true);

  useEffect(() => {
    const fetchCreditOptions = async () => {
      setCreditOptionsLoading(true);
      try {
        const result = await getCreditOptionsHandler();
        if (Array.isArray(result)) {
          setCreditOptions(result);
          const filtered = result?.filter((opt) => opt.currency === currency);
          setSelectedCreditOption(
            filtered[0] || { label: "", value: "", price: 0, currency: "usd" }
          );
        } else if (result?.data) {
          setCreditOptions(result.data);
          const filtered = result?.data?.filter(
            (opt: ICreditOption) => opt.currency === currency
          );
          setSelectedCreditOption(
            filtered[0] || { label: "", value: "", price: 0, currency: "usd" }
          );
        } else {
          setCreditOptions([]);
        }
      } catch (e) {
        setCreditOptions([]);
      }
      setCreditOptionsLoading(false);
    };
    fetchCreditOptions();
  }, []);

  useEffect(() => {
    if (isBuyCredits) setStep(EnrichmentStep.STEP_BUY_CREDITS);
    if (noCreditsLeft) setStep(EnrichmentStep.STEP_NO_CREDITS_LEFT);
  }, [isBuyCredits, noCreditsLeft]);

  const handlePaymentSuccess = async () => {
    if (isBuyCredits) {
      showSuccessToast({ message: "Credits purchased successfully." });
      try {
        setTimeout(() => {
          hideModal();
          refetchWorkspace();
        }, 3000);
      } catch (error) {
        console.error("Error updating enrichment credits:", error);
      }
      return;
    }

    if (selectedConnections?.length) {
      pollEnrichedTransactionForConnectionsById(enrichedTransactionId);
    } else if (csvRows?.length) {
      pollEnrichedTransactionForCsvById(enrichedTransactionId);
    }
  };

  const getEnrichedConnections = async (enrichedTransactionId: string) => {
    setEnrichmentInProgress(true);
    const result = await getEnrichedConnectionsHandler({
      connectionIds: isAllSelected
        ? selectedConnections
        : selectedConnections?.map((connection) => connection._id),
      enrichedTransactionId,
    });
    if (result) {
      setEnrichmentInProgress(false);
      // setTimeout(() => {
      //   hideModal();
      // }, 5000);
    }
  };

  const filteredCreditOptions = creditOptions.filter(
    (opt) => opt.currency === currency
  );

  const onChangeCurrency = (currency: string) => {
    setCurrency(currency);
    const filtered = creditOptions.filter((opt) => opt.currency === currency);
    setSelectedCreditOption(
      filtered[0] || { label: "", value: "", price: 0, currency: "usd" }
    );
    if (calculatedPrice) {
      const convertedPrice = convertCurrency(calculatedPrice, currency);
      setCalculatedPrice({ ...convertedPrice });
    }
  };

  function convertCurrency(
    priceObject: ICalculatedPrice,
    targetCurrency: string
  ) {
    const exchangeRates = {
      usd_to_eur: Number(`${process.env.REACT_APP_EXCHANGE_RATE_USD_TO_EUR}`),
      eur_to_usd: Number(`${process.env.REACT_APP_EXCHANGE_RATE_EUR_TO_USD}`),
    };

    const currentCurrency = priceObject?.currency?.toLowerCase();
    targetCurrency = targetCurrency.toLowerCase();

    if (currentCurrency === targetCurrency) {
      return priceObject;
    }

    let exchangeRate;
    if (currentCurrency === "usd" && targetCurrency === "eur") {
      exchangeRate = exchangeRates.usd_to_eur;
    } else if (currentCurrency === "eur" && targetCurrency === "usd") {
      exchangeRate = exchangeRates.eur_to_usd;
    } else {
      throw new Error("Unsupported currency conversion");
    }

    priceObject.phoneUnitPrice = priceObject.phoneUnitPrice ?? 0 * exchangeRate;
    priceObject.emailUnitPrice = priceObject.emailUnitPrice ?? 0 * exchangeRate;
    priceObject.phonePrice = priceObject.phonePrice ?? 0 * exchangeRate;
    priceObject.privateEmailPrice =
      priceObject.privateEmailPrice ?? 0 * exchangeRate;
    priceObject.businessEmailPrice =
      priceObject.businessEmailPrice ?? 0 * exchangeRate;
    priceObject.totalPrice =
      (priceObject?.totalPrice ?? 0) * exchangeRate < 1
        ? 1
        : (priceObject?.totalPrice ?? 0) * exchangeRate;
    priceObject.stripePrice =
      (priceObject?.stripePrice ?? 0) * exchangeRate < 100
        ? 100
        : (priceObject?.stripePrice ?? 0) * exchangeRate;

    priceObject.currency = targetCurrency;

    return priceObject;
  }

  const pollEnrichedTransactionForCsvById = async (
    enrichedTransactionId?: string
  ) => {
    const intervalId = setInterval(async () => {
      const result = await getEnrichedTransactionByIdHandler({
        enrichedTransactionId,
      });
      if (result?.failed) {
        clearInterval(intervalId);
        showErrorToast({ message: "Enrichment failed." });
      } else if (result?.enriched && result?.fileUrl) {
        clearInterval(intervalId);
        hideToast();
        setEnrichedTransaction(result);
        // open enrichment complete modal
        setEnrichmentCompleteModalHandler(result);
        showConfetti && showConfetti();
        setEnrichmentInProgress(true);
        setIsStripePayment(false);
        setTimeout(() => {
          setEnrichmentInProgress(false);
          downloadEnrichedCsv(result.fileUrl);
          refetchWorkspace();
        }, 3000);
      } else {
        if (result?.enrichedData) {
          enrichedConnectionsCountRef.current = result.enrichedData?.length;
          onEnrichedTransactionChange && onEnrichedTransactionChange(result);
          !toastClosedRef.current &&
            showRemovalScheduledToast({
              message: `Enriched ${enrichedConnectionsCountRef.current} of ${
                selectedConnections?.length || csvRows?.length || 0
              } contacts`,
              duration: -1,
              showSpinner: true,
              onClick: {
                keepShowingOnClick: true,
                onCloseHandler: async () => {
                  console.log("onCloseHandler");
                  toastClosedRef.current = true;
                },
              },
            });
        }
      }
    }, 1000);
  };

  const downloadEnrichedCsv = (fileUrl: string) => {
    setTimeout(() => {
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = "enriched_data.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      hideModal();
    }, 5000);
  };

  const pollEnrichedTransactionForConnectionsById = async (
    enrichedTransactionId: string
  ) => {
    const intervalId = setInterval(async () => {
      const result = await getEnrichedTransactionByIdHandler({
        enrichedTransactionId,
      });
      if (result?.failed) {
        clearInterval(intervalId);
        showErrorToast({ message: "Enrichment failed." });
      } else if (result?.enriched) {
        setCalculationInProgress(false);
        setEnrichmentInProgress(false);
        clearInterval(intervalId);
        setEnrichedTransaction(result);
        onEnrichedTransactionChange && onEnrichedTransactionChange(result);
        //setModalVisible(true);
        hideToast();
        //setStep(EnrichmentStep.STEP_PAYMENT_SUCCESS);

        // open enrichment complete modal
        setEnrichmentCompleteModalHandler(result);

        // count successfully enriched connections
        const hasPrivate = !!result?.privateEmailsCountAfterEnrichment;
        const hasBusiness = !!result?.businessEmailsCountAfterEnrichment;
        const hasPhone = !!result?.phonesCountAfterEnrichment;

        if (selectedConnections?.length && selectedConnections?.length > 1) {
          if (hasPrivate || hasBusiness || hasPhone) {
            showConfetti && showConfetti();
          }
        } else {
          if (selectedConnections?.length === 1) {
            if (!hasPrivate && !hasBusiness && !hasPhone) {
              const notFound = [];
              if (privateEmailCheckedState) notFound.push("personal email");
              if (businessEmailCheckedState) notFound.push("business email");
              if (phoneCheckedState) notFound.push("phone number");
              let message = "No ";
              if (notFound.length === 1) {
                message += `${notFound[0]} found.`;
              } else if (notFound.length === 2) {
                message += `${notFound[0]} or ${notFound[1]} found.`;
              } else if (notFound.length === 3) {
                message += `${notFound[0]}, ${notFound[1]} or ${notFound[2]} found.`;
              } else {
                message += "enrichment data found.";
              }
              showErrorToast({ message });
            } else {
              let message = `${selectedConnections?.length} contact successfully enriched`;
              const details: string[] = [];
              if (hasPrivate) {
                details.push(
                  `${result?.privateEmailsCountAfterEnrichment} personal email`
                );
              }
              if (hasBusiness) {
                details.push(
                  `${result?.businessEmailsCountAfterEnrichment} business email`
                );
              }
              if (hasPhone) {
                details.push(`${result?.phonesCountAfterEnrichment} phone`);
              }
              if (details.length > 0) {
                message += ` (${details.join(", ")}).`;
              }
              showSuccessToast({
                message: message,
              });
            }
          }
          // hideModal();
        }
        setIsStripePayment(false);
        // update connections
        getEnrichedConnections(result?._id);
        refetchWorkspace();
      } else {
        if (result?.enrichedData) {
          enrichedConnectionsCountRef.current = result.enrichedData?.length;
          onEnrichedTransactionChange && onEnrichedTransactionChange(result);
          !toastClosedRef.current &&
            showRemovalScheduledToast({
              message: `Enriched ${enrichedConnectionsCountRef.current} of ${
                selectedConnections?.length || csvRows?.length || 0
              } contacts`,
              duration: -1,
              showSpinner: true,
              onClick: {
                keepShowingOnClick: true,
                onCloseHandler: async () => {
                  console.log("onCloseHandler");
                  toastClosedRef.current = true;
                },
              },
            });
        }
      }
    }, 1000);
  };

  const estimateEnrichmentCredits = async () => {
    setCalculationInProgress(true);
    setEstimatingCreditsDone(false);
    const result = await estimateEnrichmentCreditsHandler({
      connectionIds: isAllSelected
        ? selectedConnections
        : selectedConnections?.map((connection) => connection._id),
      csvRows: csvRows,
      privateEmailChecked: privateEmailCheckedState,
      businessEmailChecked: businessEmailCheckedState,
      phoneChecked: phoneCheckedState,
    });
    if (result?.estimatedCredits) {
      setEstimatedCredits(result?.estimatedCredits);
      setEstimatingCreditsDone(true);
    }
    if (result?.enrichedTransactionId) {
      setEnrichedTransactionId(result?.enrichedTransactionId);
    }
    setCalculationInProgress(false);
  };

  const goToByCredits = () => {
    if (creditOptions.length > 0) {
      const filtered = creditOptions?.filter(
        (opt) => opt.currency === currency
      );
      setSelectedCreditOption(
        filtered[0] || { label: "", value: "", price: 0, currency: "usd" }
      );
    }
    setStep(EnrichmentStep.STEP_BUY_CREDITS);
  };

  const enrichConnections = async () => {
    hideModal();
    onClickHandler(
      selectedConnections || [],
      !!privateEmailCheckedState,
      !!businessEmailCheckedState,
      !!phoneCheckedState,
      enrichedTransactionId
    );
    showRemovalScheduledToast({
      message: `Enriched ${enrichedConnectionsCountRef.current} of ${
        (selectedConnections && selectedConnections.length) ||
        (csvRows && csvRows.length) ||
        0
      } contacts`,
      duration: -1,
      showSpinner: true,
      onClick: {
        keepShowingOnClick: true,
        onCloseHandler: async () => {
          toastClosedRef.current = true;
        },
      },
    });
    //setModalVisible(false);
    setCalculationInProgress(true);
    setEnrichmentInProgress(true);
    const result = await enrichConnectionsHandler({
      enrichedTransactionId,
    });
    if (result?.enrichedTransactionId) {
      if (selectedConnections?.length) {
        pollEnrichedTransactionForConnectionsById(result.enrichedTransactionId);
      } else if (csvRows?.length) {
        pollEnrichedTransactionForCsvById(result.enrichedTransactionId);
      }
    }
  };

  const proceedToCreditsPayment = async () => {
    setCalculationInProgress(true);
    setStep(EnrichmentStep.STEP_PAYMENT_DETAILS);
    setIsStripePayment(true);

    let result;
    if (isBuyCredits || noCreditsLeft) {
      result = await buyCreditsInvoiceWithoutEnrichedTransactionHandler({
        // Changed from PaymentIntent to Invoice
        //creditPrice: selectedCreditOption.price,
        currency: currency ?? "usd",
        priceId: selectedCreditOption.priceId,
        credits: selectedCreditOption?.value
          ? parseInt(selectedCreditOption?.value)
          : undefined,
      });
    } else {
      result = await buyCreditsInvoiceHandler({
        // Changed from PaymentIntent to Invoice
        enrichedTransactionId: enrichedTransactionId,
        //creditPrice: selectedCreditOption.price,
        currency: currency ?? "usd",
        priceId: selectedCreditOption.priceId,
      });
    }
    if (result?.invoiceId) {
      setInvoiceId(result?.invoiceId);
      setCalculatedPrice({
        ...calculatedPrice,
        stripePrice: result?.stripePrice,
        currency: currency ?? "usd",
      });

      // prepare setup intent (required for new card payment)
      const resultSetupIntent = await getSetupIntentHandler();
      if (resultSetupIntent?.client_secret) {
        setClientSecret(resultSetupIntent.client_secret);
      }
    }
    setCalculationInProgress(false);
  };

  const InfoComponent: React.FC<{ width?: string; description: string }> = ({
    width = "210px",
    description,
  }) => {
    return (
      <Info>
        <StyledSvg>
          <SvgFragment type="infoCircle" />
        </StyledSvg>
        <InfoDescription
          width={width}
          className="info-description-enrichment-modal"
        >
          {description}
        </InfoDescription>
      </Info>
    );
  };

  const onModalCancelHandler = () => {
    if (step === EnrichmentStep.STEP_PAYMENT_DETAILS && invoiceId) {
      voidInvoiceRequestHandler(invoiceId);
    }
    hideModal();
  };

  return step === EnrichmentStep.STEP_CHECK_FIELDS ? (
    <CustomModal
      headingText={
        estimatingCreditsDone
          ? "Enrichment overview"
          : "Choose contact details to enrich"
      }
      closeButton
      confirmButton={{
        text: estimatingCreditsDone ? "Enrich" : "Continue",
        loadingText: enrichmentInProgress
          ? "Enrich"
          : calculationInProgress
          ? "Continue"
          : "",
        customDisabled: styles.colors.primary400,
        type: "blue",
        mediumBigButton: true,
        isLoading: calculationInProgress,
        disable: estimatingCreditsDone && workspace.enrichmentCredits <= 0,
        onClickHandler: () =>
          estimatingCreditsDone
            ? enrichConnections()
            : estimateEnrichmentCredits(),
      }}
      cancelButton={{
        text: estimatingCreditsDone ? "Back" : "Cancel",
        mediumButton: true,
        onClickHandler: () =>
          estimatingCreditsDone ? setEstimatingCreditsDone(false) : hideModal(),
      }}
      //visible={modalVisible}
    >
      {estimatingCreditsDone ? (
        <Column gap="10px">
          <Row gap="10px" alignItems spaceBetween style={{ height: "20px" }}>
            <Row gap="10px" alignItems>
              <Text>Contacts to enrich:</Text>
              <Text $bold>
                {selectedConnections?.length || csvRows?.length || 0}
              </Text>
            </Row>
          </Row>
          <Row gap="10px" alignItems spaceBetween style={{ height: "20px" }}>
            <Row gap="8px" alignItems style={{ alignItems: "center" }}>
              <Text>Estimated credit usage:</Text>
              <Text $bold>{estimatedCredits}</Text>
              <InfoComponent description="This is just an estimate. Credits are only used for successful searches." />
            </Row>
          </Row>
          <Column gap="20px">
            <Row gap="10px" alignItems style={{ height: "20px" }}>
              <Row gap="10px" alignItems>
                <Text>Available credits:</Text>
                <Text $bold>{workspace.enrichmentCredits}</Text>
              </Row>
              <ActionText onClickHandler={() => goToByCredits()} $heading5>
                Buy more credits
              </ActionText>
            </Row>
            {workspace.enrichmentCredits <= estimatedCredits && (
              <Row
                style={{
                  backgroundColor: styles.colors.yellow300,
                  padding: "4px 8px",
                  borderRadius: "4px",
                }}
              >
                <Text>
                  You don't have enough credits to enrich all connections.{" "}
                  {workspace.enrichmentCredits > 0 &&
                  Math.floor(
                    (workspace.enrichmentCredits / estimatedCredits) *
                      (selectedConnections?.length || csvRows?.length || 1)
                  ) > 0 ? (
                    <>
                      You can still proceed with the Enrich for approximately{" "}
                      {Math.floor(
                        (workspace.enrichmentCredits / estimatedCredits) *
                          (selectedConnections?.length || csvRows?.length || 1)
                      )}{" "}
                      connections or buy more credits.
                    </>
                  ) : (
                    <>
                      You need to buy more credits if you want to proceed with
                      the Enrich.
                    </>
                  )}
                </Text>
              </Row>
            )}
          </Column>
        </Column>
      ) : (
        <Column gap="10px">
          <Column gap="4px">
            <Row style={{ marginBottom: "12px" }}>
              <StyledText>
                {"You're about to enrich "}
                <p style={{ display: "inline", fontWeight: "bold" }}>
                  {selectedConnections?.length || csvRows?.length || 0}{" "}
                  {selectedConnections?.length
                    ? selectedConnections.length === 1
                      ? "contact"
                      : "contacts"
                    : csvRows?.length === 1
                    ? "row"
                    : "rows"}
                </p>
                {". Select the contact details you'd like to enrich:"}
              </StyledText>
            </Row>
            <Row
              style={{
                padding: "8px 12px",
                justifyContent: "space-between",
                alignItems: "center",
                border: `1px solid ${styles.colors.black200}`,
                borderRadius: "4px",
              }}
            >
              <Row gap="4px" alignItems>
                <Row gap="8px" alignItems>
                  <Checkbox
                    $height="auto"
                    checked={businessEmailCheckedState}
                    checkboxId={"businessEmailChecked"}
                    onClickHandler={() =>
                      setBusinessEmailChecked(!businessEmailCheckedState)
                    }
                  />
                  <Text $bold>{"Business email"}</Text>
                </Row>
                <InfoComponent description="Find a verified business email e.g., name@company.com. Credits are used only when we find an email." />
              </Row>
              <Row alignItems>
                <Text>-1 credit/field</Text>
              </Row>
            </Row>
            <Row
              style={{
                padding: "8px 12px",
                alignItems: "center",
                justifyContent: "space-between",
                border: `1px solid ${styles.colors.black200}`,
                borderRadius: "4px",
              }}
            >
              <Row gap="4px" alignItems>
                <Row gap="8px" alignItems>
                  <Checkbox
                    $height="auto"
                    checked={privateEmailCheckedState}
                    checkboxId={"privateEmailChecked"}
                    onClickHandler={() =>
                      setPrivateEmailChecked(!privateEmailCheckedState)
                    }
                  />
                  <Text $bold>{"Personal email"}</Text>
                </Row>
                <InfoComponent description="Find a verified personal email (e.g., Gmail, Yahoo, Outlook). Credits are used only when we find an email." />
              </Row>
              <Row alignItems>
                <Text>-3 credits/field</Text>
              </Row>
            </Row>
            <Row
              style={{
                padding: "8px 12px",
                alignItems: "center",
                justifyContent: "space-between",
                border: `1px solid ${styles.colors.black200}`,
                borderRadius: "4px",
              }}
            >
              <Row gap="4px" alignItems>
                <Row gap="8px" alignItems>
                  <Checkbox
                    $height="auto"
                    checked={phoneCheckedState}
                    checkboxId={"phoneChecked"}
                    onClickHandler={() => setPhoneChecked(!phoneCheckedState)}
                  />
                  <Text $bold>{"Phone number"}</Text>
                </Row>
                <InfoComponent description="Find a verified phone number linked to the contact. Credits are used only when we find a phone number." />
              </Row>
              <Row alignItems>
                <Text>-10 credits/field</Text>
              </Row>
            </Row>
          </Column>
        </Column>
      )}
    </CustomModal>
  ) : step === EnrichmentStep.STEP_BUY_CREDITS ? (
    <CustomModal
      headingText={"Select credits"}
      closeButton
      confirmButton={{
        text: "Proceed to payment",
        type: "blue",
        customWidth: true,
        onClickHandler: () => proceedToCreditsPayment(),
        disable: creditOptionsLoading || creditOptions.length === 0,
      }}
      cancelButton={{
        text: isBuyCredits || noCreditsLeft ? "Cancel" : "Back",
        mediumButton: true,
        onClickHandler: () =>
          isBuyCredits || noCreditsLeft
            ? hideModal()
            : setStep(EnrichmentStep.STEP_CHECK_FIELDS),
      }}
      //visible={modalVisible}
    >
      <Column gap="10px">
        <Text>Select the number of credits below.:</Text>
        <section>
          {creditOptionsLoading ? (
            <LoadingSpinner height="40px" width="40px" />
          ) : (
            <StyledSelect
              options={filteredCreditOptions}
              value={
                filteredCreditOptions.find(
                  (opt) => opt.value === selectedCreditOption.value
                ) || filteredCreditOptions[0]
              }
              onChange={(e: any) => setSelectedCreditOption(e)}
              isSearchable={false}
              formatOptionLabel={(option: any) => {
                const match = option.label.match(/^(.*?\))\s*\((.*?)\)$/);
                const main = match?.[1] || option.label;
                const discount = match?.[2];
                return (
                  <Row alignItems>
                    {main}
                    {discount && (
                      <span style={{ fontWeight: "bold", marginLeft: 4 }}>
                        <Tag
                          title={discount}
                          $borderColor={styles.colors.white}
                          $color={styles.colors.green600}
                        />
                      </span>
                    )}
                  </Row>
                );
              }}
            />
          )}
        </section>
        <div style={{ position: "absolute", bottom: "-25px" }}>
          <CurrencySelect
            prefilledValue={currency}
            onChangeHandler={onChangeCurrency}
          />
        </div>
      </Column>
    </CustomModal>
  ) : step === EnrichmentStep.STEP_PAYMENT_DETAILS ? (
    <CustomModal
      headingText={"Payment"}
      closeButton
      onCancelHandler={onModalCancelHandler}
      //visible={modalVisible}
    >
      {calculationInProgress ? (
        <Row
          style={{
            flexDirection: "column",
            alignItems: "flex-start",
            width: "100%",
          }}
        >
          <Column gap="10px">
            <Text>{`We are preparing your payment...`}</Text>
          </Column>
          <Row
            style={{
              justifyContent: "center",
              width: "100%",
              paddingTop: "30px",
            }}
          >
            <LoadingSpinner height="40px" width="40px" />
          </Row>
        </Row>
      ) : (
        <>
          <Row style={{ marginBottom: "10px" }}>
            <div style={{ fontSize: "20px", fontWeight: "bold" }}>
              Total to pay:{" "}
              {currency === "usd"
                ? `${formatCurrency(currency)}${formatPriceTwoDecimals(
                    calculatedPrice?.stripePrice || 0
                  )}`
                : `${formatPriceTwoDecimals(
                    calculatedPrice?.stripePrice || 0
                  )}${formatCurrency(currency)}`}
            </div>
          </Row>
          <Row>
            <Text>{"Please enter your payment details:"}</Text>
          </Row>
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PaymentForm
              invoiceId={invoiceId}
              onSuccess={handlePaymentSuccess}
              selectedPaymentMethodId={selectedPaymentMethodId}
              setSelectedPaymentMethodId={setSelectedPaymentMethodId}
            />
          </Elements>
        </>
      )}
    </CustomModal>
  ) : step === EnrichmentStep.STEP_PAYMENT_SUCCESS ? (
    <CustomModal
      headingText={
        enrichmentInProgress
          ? isStripePayment
            ? "Payment successful"
            : "Enrichment complete! 🎉"
          : "Enrichment complete! 🎉"
      }
      closeButton
      //visible={modalVisible}
    >
      <Row
        gap="10px"
        style={{
          width: "100%",
        }}
      >
        <Column gap="16px" style={{ width: "100%" }}>
          <Row gap="4px" alignItems spaceBetween>
            <Text>Overview</Text>
            <Row gap="16px" alignItems>
              <Row
                style={{
                  width: "58px",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <SvgFragment type="checkGreen" />
              </Row>
              <Row
                style={{
                  width: "58px",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <SvgFragment type="errorLight" />
              </Row>
            </Row>
          </Row>
          <Row
            style={{
              width: "100%",
              height: "1px",
              backgroundColor: styles.colors.black200,
            }}
          />
          <Column gap="8px" justifyCenter>
            <Row gap="4px" alignItems spaceBetween>
              <Text>{"Business emails"}</Text>
              <Row gap="16px" alignItems>
                <Row
                  style={{
                    width: "58px",
                    display: "flex",
                    justifyContent: "flex-end",
                    paddingRight: "4px",
                  }}
                >
                  <Text>
                    {enrichedTransaction?.businessEmailsCountAfterEnrichment ||
                      0}
                  </Text>
                </Row>
                <Row
                  style={{
                    width: "58px",
                    display: "flex",
                    justifyContent: "flex-end",
                    paddingRight: "8px",
                  }}
                >
                  <Text>
                    {enrichedTransaction?.businessEmailsCount
                      ? enrichedTransaction?.businessEmailsCount -
                        enrichedTransaction?.businessEmailsCountAfterEnrichment
                      : 0}
                  </Text>
                </Row>
              </Row>
            </Row>
            <Row gap="4px" alignItems spaceBetween>
              <Text>{"Personal emails"}</Text>
              <Row gap="16px" alignItems>
                <Row
                  style={{
                    width: "58px",
                    display: "flex",
                    justifyContent: "flex-end",
                    paddingRight: "4px",
                  }}
                >
                  <Text>
                    {enrichedTransaction?.privateEmailsCountAfterEnrichment ||
                      0}
                  </Text>
                </Row>
                <Row
                  style={{
                    width: "58px",
                    display: "flex",
                    justifyContent: "flex-end",
                    paddingRight: "8px",
                  }}
                >
                  <Text>
                    {enrichedTransaction?.privateEmailsCount
                      ? enrichedTransaction?.privateEmailsCount -
                        enrichedTransaction?.privateEmailsCountAfterEnrichment
                      : 0}
                  </Text>
                </Row>
              </Row>
            </Row>
            <Row gap="4px" alignItems spaceBetween>
              <Text>{"Phone numbers"}</Text>
              <Row gap="16px" alignItems>
                <Row
                  style={{
                    width: "58px",
                    display: "flex",
                    justifyContent: "flex-end",
                    paddingRight: "4px",
                  }}
                >
                  <Text>
                    {enrichedTransaction?.phonesCountAfterEnrichment || 0}
                  </Text>
                </Row>
                <Row
                  style={{
                    width: "58px",
                    display: "flex",
                    justifyContent: "flex-end",
                    paddingRight: "8px",
                  }}
                >
                  <Text>
                    {enrichedTransaction?.phonesCount
                      ? enrichedTransaction?.phonesCount -
                        enrichedTransaction?.phonesCountAfterEnrichment
                      : 0}
                  </Text>
                </Row>
              </Row>
            </Row>
          </Column>
        </Column>
      </Row>
    </CustomModal>
  ) : step === EnrichmentStep.STEP_NO_CREDITS_LEFT ? (
    <CustomModal
      headingText={"You have no credits left"}
      headingTextCenter
      closeButton
      $center
      confirmButton={{
        text: "Buy credits",
        type: "blue",
        customWidth: true,
        onClickHandler: () => {
          setStep(EnrichmentStep.STEP_BUY_CREDITS);
        },
      }}
      //visible={modalVisible}
    >
      <Column gap="10px" style={{ textAlign: "center" }}>
        <Text>
          You used all your enrichment credits. To continue enriching
          connections, please purchase more credits.
        </Text>
      </Column>
    </CustomModal>
  ) : null;
};

const StyledText = styled(Text)`
  display: inline-block;
  text-align: left;
  font-weight: normal;
`;
const Info = styled.div`
  position: relative;
  cursor: pointer;
  height: 24px;

  &:hover .info-description-enrichment-modal {
    display: block;
  }
`;
const InfoDescription = styled.div<{ width: string }>`
  display: none;
  width: ${({ width }) => width};
  position: absolute;
  background-color: ${styles.colors.white};
  box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.3);
  padding: 5px;
  border-radius: 5px;
  left: 22px;
  top: 22px;
  z-index: 2;
`;
const StyledSelect = styled(Select)`
  [class$="-Menu"] {
    width: 450px;
  }
`;
const StyledSvg = styled.div`
  &:hover {
    cursor: pointer;
  }
  & svg {
    & path {
      fill: ${styles.colors.black300};
    }
  }
`;
const PaymentForm = ({
  invoiceId,
  onSuccess,
  selectedPaymentMethodId,
  setSelectedPaymentMethodId,
}: {
  invoiceId: string;
  onSuccess: (invoiceId?: string) => void;
  selectedPaymentMethodId: string | undefined;
  setSelectedPaymentMethodId: (paymentMethodId: string) => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);
  const [showNewCardForm, setShowNewCardForm] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);

  const {
    store: { subscription },
  } = useAuthStore();

  const { payInvoiceRequestHandler, addPaymentMethodHandler } =
    useBillingHandlers();

  useEffect(() => {
    if (subscription?.paymentMethods?.length === 0) {
      setShowNewCardForm(true);
    }
  }, [subscription]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setProcessing(true);
    try {
      if (showNewCardForm && stripe && elements) {
        // 1. Confirm setup intent to save the card
        const { error: confirmError, setupIntent } = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: "if_required",
        });

        if (confirmError) {
          setError(confirmError.message || "Failed to save card");
          return;
        }

        if (setupIntent?.payment_method) {
          // 2. Attach the new payment method to the customer
          await addPaymentMethodHandler(
            setupIntent.payment_method as string,
            true,
            false
          );

          // 3. Pay the invoice with the new method
          const payResult = await payInvoiceRequestHandler(
            invoiceId,
            setupIntent.payment_method as string
          );

          if (payResult?.success) {
            onSuccess(invoiceId);
          } else {
            setError(payResult?.error || "Payment failed");
          }
        }
      } else if (selectedPaymentMethodId) {
        // Pay with existing payment method
        const result = await payInvoiceRequestHandler(
          invoiceId,
          selectedPaymentMethodId
        );

        if (result?.success) {
          onSuccess(invoiceId);
        } else {
          setError(result?.error || "Payment failed");
        }
      }
    } catch (err) {
      setError("Payment processing failed");
    }
  };

  const toggleNewCardForm = () => {
    setShowNewCardForm(!showNewCardForm);
  };

  const onSelectedPaymentMethodHandler = (paymentMethodId: string) => {
    setSelectedPaymentMethodId(paymentMethodId);
  };

  return (
    <div style={{ marginTop: "20px", padding: "20px" }}>
      <form onSubmit={handleSubmit}>
        {subscription?.paymentMethods?.length > 0 && (
          <div style={{ marginBottom: "10px" }}>
            <Column gap="10px">
              <PaymentMethods
                disabled={showNewCardForm}
                onChangeHandler={onSelectedPaymentMethodHandler}
              />

              <ActionText onClickHandler={toggleNewCardForm} $heading5>
                {showNewCardForm ? "Cancel" : "Add another one"}
              </ActionText>
            </Column>
          </div>
        )}

        {(showNewCardForm || !subscription?.paymentMethods?.length) && (
          <Column gap="20px">
            <PaymentElement
              className="ld-stripe-element"
              onChange={(e) => setCardComplete(e.complete)}
            />
          </Column>
        )}
        <button
          type="submit"
          disabled={processing || (showNewCardForm && !cardComplete)}
          style={{
            padding: "10px 20px",
            marginTop: "20px",
            background:
              processing || (showNewCardForm && !cardComplete)
                ? "#7ab3ff"
                : "#007bff",
            fontSize: "16px",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor:
              processing || (showNewCardForm && !cardComplete)
                ? "not-allowed"
                : "pointer",
            width: "100%",
          }}
        >
          {processing ? (
            <Row gap="5px" alignItems justifyCenter>
              <LoadingSpinner color={styles.colors.white} />
              <LoadingText>Payment in progress</LoadingText>
            </Row>
          ) : (
            "Pay Now"
          )}
        </button>
        {error && (
          <div style={{ color: "#dc3545", marginTop: "10px" }}>{error}</div>
        )}
      </form>
    </div>
  );
};

const LoadingText = styled(Text)<{ $color?: string }>`
  color: ${({ $color }) => ($color ? $color : styles.colors.white)};
  font-size: 16px;
`;

export default CalculateEnrichmentModal;
